import React, { useState } from "react";
import { Alert, Button } from "react-bootstrap";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { useApiGet, useApi } from "../../../hooks/useApi";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARDHOLDER_VERIFICATION_MESSAGE =
  "This card requires verification by the cardholder (3D Secure / bank authentication). An admin cannot complete that step. Ask the customer to add the card from their account, or try a different card.";

const isCardholderVerificationRequired = (error, setupIntent) => {
  if (setupIntent?.status === "requires_action" || setupIntent?.next_action) {
    return true;
  }
  if (!error) {
    return false;
  }
  const code = `${error.code || ""} ${error.decline_code || ""}`.toLowerCase();
  const message = (error.message || "").toLowerCase();
  return (
    code.includes("authentication") ||
    message.includes("authenticate") ||
    message.includes("authentication") ||
    message.includes("3d secure") ||
    message.includes("redirect")
  );
};

const SetupForm = ({ buttonLabel, onComplete, setupIntentId, userId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [formReady, setFormReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { call: confirmSetup, isLoading: confirming } = useApi({
    onSuccess: (res) => {
      setSubmitting(false);
      onComplete(res);
    },
    onError: (err) => {
      setSubmitting(false);
      setErrorMessage(err.message);
    },
  });

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    // Never leave the admin page for bank redirects; surface a clear error instead.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });

    if (error || setupIntent?.status === "requires_action") {
      setErrorMessage(
        isCardholderVerificationRequired(error, setupIntent)
          ? CARDHOLDER_VERIFICATION_MESSAGE
          : error?.message || CARDHOLDER_VERIFICATION_MESSAGE
      );
      setSubmitting(false);
      return;
    }

    const intentId = setupIntent?.id || setupIntentId;
    confirmSetup(
      `/admin/users/${userId}/setup-intent/${intentId}`,
      "put"
    );
  };

  return (
    <form onSubmit={onSubmit}>
      {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
      <PaymentElement onReady={() => setFormReady(true)} />
      <br />
      <Button
        type="submit"
        variant="primary"
        className="w-100"
        disabled={!stripe || !elements || !formReady || submitting || confirming}
      >
        {buttonLabel}
      </Button>
    </form>
  );
};

/**
 * Admin payment collection for a newly created customer.
 * Reuses CreditCardForm / Stripe Elements patterns with admin setup-intent endpoints.
 */
const AdminCustomerPaymentForm = ({
  userId,
  buttonLabel = "Save Payment Method",
  onComplete,
}) => {
  const { isLoading, data, isError, error } = useApiGet(
    `admin-setup-intent-${userId}`,
    `/admin/users/${userId}/setup-intent`,
    null,
    { staleTime: 0, cacheTime: 0 }
  );

  if (isLoading) {
    return <p>Setting up payment...</p>;
  }

  if (isError) {
    return (
      <Alert variant="danger">
        Could not set up payment. Please try again.
        <br />
        {error?.message}
      </Alert>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: data.clientSecret,
      }}
    >
      <SetupForm
        buttonLabel={buttonLabel}
        onComplete={onComplete}
        setupIntentId={data.setupIntentId}
        userId={userId}
      />
    </Elements>
  );
};

export default AdminCustomerPaymentForm;
